<template>
	<div class="question-container">
		<div v-if="!data.isSavingDeal">
			<div
				class="question-text"
				v-for="(question, index) in data.question.getQuestion()"
				v-html="question"
				:key="index"
			></div>
			<div
				class="note-text"
				:style="{ color: theme.lightMainTextColor }"
				v-for="(note, index) in data.question.getNotes()"
				v-html="note"
				:key="index + 100"
			></div>
			<div
				v-if="data.question.answerButtons.length > 0"
				class="input-text"
			>
				<div></div>
				<button
					class="button"
					:style="{
						background: theme[answer.style.background],
						color: theme[answer.style.text],
					}"
					@click="answer.actions(textValue, [], data.question, toggleLoading, saveApplicationAction)"
				>
					{{ data.question.getLocalizationDisplay(answer.label, data.language) }}
				</button>
			</div>
		</div>
		<LoaderComponent v-else></LoaderComponent>
	</div>
</template>

<script>
	import api from '@core/services/api/_api';
	import DocumentAssociation from '@core/classes/DocumentAssociation';
	import ENUMS from '@core/classes/Enums';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import LOCALIZATION from '@core/classes/Localization';
	import PlaidGenerateLinkRequest from '@core/classes/Plaid/PlaidGenerateLinkRequest';
	import PlaidLinkConfig from '@core/classes/Plaid/PlaidLinkConfig';
	import { PlaidUserInfo } from '@core/classes/Plaid/PlaidUserInfo';
	import { redirectToCustomerPortal } from '@core/helpers/Customer/customer-helper';
	import settings from 'settings';

	export default {
		name: 'QuestionTextMessage',
		props: ['theme', 'data', 'questionKey', 'toggleLoading', 'saveApplicationAction'],
		data() {
			return {
				answer: {},
				textValue: null,
			};
		},
		computed: {
			localization() {
				return LOCALIZATION;
			},
			buttonDisabled() {
				return this.textValue == '' || this.textValue == null;
			},
			ENUMS() {
				return ENUMS;
			},
		},
		async created() {
			this.answer = this.data.question.getAnswers()[0];
			this.textValue = this.data.question.savedAnswer;
			this.$watch(
				() => this.data.language,
				newValue => {
					this.data.question.customerApp().quoteAppData.changeLanguage(newValue);
				},
				{ deep: true },
			);

			if (this.data.question.redirectToCustomerPortal == true || this.data.question.isEndOfApp == true) {
				setTimeout(async () => {
					const linkForRedirect = await redirectToCustomerPortal(this.data.application.customerId);
					window.location.href = linkForRedirect;
				});
			}

			if (this.data.question.redirectToPlaid == true) {
				const customerAssociation = await this.getCustomerAssociation([ENUMS.DOC_ASSOCIATION.PlaidIDVSession], this.data.question.customerApp().customerId);
				const hasIDV = customerAssociation != null ? true : false;
				if (!hasIDV && !this.data.application.quoteAppData.applicationsDataAnswered.isBusiness) {
					this.generatePlaidPortalLink();
				}
			}
		},
		mounted() {},
		methods: {
			async generatePlaidPortalLink() {
				this.isBusy = true;
				try {
					// Create Plaid Config
					const plaidUserInfo =
						settings.plaidEnvironment !== 'SANDBOX' ? PlaidUserInfo.getUserFromCustomerApplication(this.data.application) : PlaidUserInfo.getSandboxUser();

					const plaidConfig = new PlaidLinkConfig({
						name: 'Saturn',
						countries: [ENUMS.PLAID_COUNTRIES.UnitedStates],
						linkLanguage: ENUMS.PLAID_LANGUAGES.English,
						productsToInit: [ENUMS.PLAID_PRODUCTS.IdentityVerification],
						userData: plaidUserInfo,
					});

					// Generate Link Token
					const prepopResponse = await plaidConfig.prepopulateIdentityVerification(this.data.application.customerId, this.data.application.storeCode);
					if (!prepopResponse?.data || prepopResponse.data.error) {
						const errorCause = prepopResponse?.data?.error ?? 'No response from server.';
						throw new Error('An error occured when prepopulating the IDV session.', { cause: errorCause });
					}

					const linkTokenResponse = await plaidConfig.createLinkToken({
						customerId: this.data.application.customerId,
					});
					if (!linkTokenResponse?.data || linkTokenResponse.data.error) {
						const errorCause = linkTokenResponse?.data?.error ?? 'No response from server.';
						throw new Error('An error occured when generating the link token.', {
							cause: errorCause,
						});
					}

					const linkToken = linkTokenResponse.data.linkToken;

					// Create Document Association
					const customerAssociation = new DocumentAssociation({
						id: this.data.application.customerId,
						type: ENUMS.DOC_ASSOCIATION.Customer,
					});

					// Generate Plaid Portal Link
					const request = new PlaidGenerateLinkRequest(linkToken, [customerAssociation], this.data.application.storeCode);
					const generatedLink = await request.getPortalLink();
					window.location.href = generatedLink;
				} catch (err) {
					console.error('An error occured when generating the Plaid Portal Link.', { cause: err });
				} finally {
					this.isBusy = false;
				}
			},
			async getCustomerAssociation(targetAssociations, customerId) {
				let response = null;

				try {
					response = await api.associations.getAssociationRelationships({
						source: {
							id: customerId,
							type: ENUMS.DOC_ASSOCIATION.Customer,
						},
						targetTypes: targetAssociations,
						isBidirectional: true,
						sourceIsFromField: false,
					});

					return response.data;
				} catch (err) {
					console.error(err);
				}
				return response;
			},
		},
		components: {
			LoaderComponent,
		},
	};
</script>

<style>
	.question-container .spinner-container {
		background: white;
	}
</style>
